import React, { Fragment } from 'react';
import { graphql } from 'react-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import {
	QuickReloadSubscriber,
	ReloadType,
} from '@confluence/quick-reload/entry-points/subscription.ts';

import { LazyRendererContentStateComponent } from '../lazyLoadables.ts';

import { StyledBorder } from './StyledBorder.tsx';
import type {
	RendererContentStateQuery$data,
	RendererContentStateQuery$variables,
} from './__generated__/RendererContentStateQuery.graphql.ts';

type RendererContentStateProps = {
	contentId: string;
	isDraft: boolean;
	hasSeparator?: boolean;
};

export const RendererContentStateImpl = ({
	contentId,
	isDraft,
	hasSeparator,
}: RendererContentStateProps) => {
	const { data, refetch } = useQueryInterop<
		RendererContentStateQuery$data,
		RendererContentStateQuery$variables
	>(
		graphql`
			query RendererContentStateQuery(
				$contentId: ID!
				$isDraft: Boolean!
				$format: GraphQLDateFormat
			) {
				singleContent(id: $contentId) {
					id
					contentState(isDraft: $isDraft) {
						id
						name
						color
						restrictionLevel
					}
					contentStateLastUpdated(format: $format) {
						value
					}
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
			variables: {
				contentId,
				isDraft, // page preview uses draft version
				format: 'USER_FRIENDLY',
			},
		},
	);

	if (!data) {
		return null;
	}

	const lastUpdatedInfo = data?.singleContent?.contentStateLastUpdated?.value;
	const contentState = data?.singleContent?.contentState;

	return (
		<Fragment>
			{contentState && (
				<>
					<LazyRendererContentStateComponent
						contentId={contentId}
						contentState={contentState}
						lastUpdatedInfo={lastUpdatedInfo}
						isDraft={isDraft}
					/>
					{!isDraft && hasSeparator && <StyledBorder />}
				</>
			)}
			<QuickReloadSubscriber name="content-state" types={[ReloadType.content]} reload={refetch} />
		</Fragment>
	);
};

export const RendererContentState = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXTENSIONS,
})(RendererContentStateImpl);
